import { ReactNode } from 'react';
import { cn } from '../../utils';
import { useMergedState } from 'rc-util';
import Button from '../Button/Button';
import { ensureButtons } from '../Button/ensureButton.fn';

type ButtonGroupProps = {
    defaultIndex?: number;
    activeIndex?: number;
    children: ReactNode;
};

export default function ButtonGroup({
    activeIndex,
    defaultIndex,
    children,
    ...rest
}: ButtonGroupProps) {
    const [mergedActiveKey, setMergedActiveKey] = useMergedState<
        number | undefined
    >(0, {
        defaultValue: defaultIndex,
        value: activeIndex && activeIndex < 0 ? undefined : activeIndex,
    });

    if (!ensureButtons(children)) {
        console.error('Children of <ButtonGroup> must be <Button>s');
        return null;
    }

    return (
        <span
            className="alli-isolate alli-inline-flex alli-rounded-md alli-shadow-sm"
            {...rest}>
            {children &&
                Array.isArray(children) &&
                children.map((child, index) => {
                    const isLast = children.length - 1 === index;
                    const { props } = child;
                    return (
                        <Button
                            key={`button-group-${index}`}
                            {...props}
                            variant="tertiary"
                            onClick={() => {
                                if (index === mergedActiveKey) {
                                    setMergedActiveKey(undefined);
                                } else {
                                    setMergedActiveKey(index);
                                }
                                props && props?.onClick();
                            }}
                            className={cn(
                                'alli-relative alli-inline-flex alli-items-center alli-bg-white alli-px-3 alli-py-2 alli-text-sm alli-font-medium alli-text-blue-gray-700 alli-ring-1 alli-ring-inset alli-ring-gray-400 hover:alli-bg-gray-100 focus:alli-z-10 active:alli-z-0',
                                {
                                    'alli-z-10 alli-text-blue-600 alli-ring-blue-600 active:alli-z-10 active:alli-ring-blue-600':
                                        index === mergedActiveKey,
                                    'alli-rounded-l-md alli-rounded-r-none':
                                        index === 0,
                                    '-alli-ml-px alli-rounded-l-none alli-rounded-r-none ':
                                        index > 0,
                                    'alli-rounded-l-none alli-rounded-r-md':
                                        isLast,
                                },
                            )}
                        />
                    );
                })}
        </span>
    );
}
